/**
 * Wishlist System for Renaissance Tea
 * - Uses localStorage for guests
 * - Uses customer metafields for logged-in users
 * - Syncs localStorage to metafields on login
 */

window.Wishlist = (function() {
  const STORAGE_KEY = 'renaissance_wishlist';
  const SYNC_ENDPOINT = '/apps/wishlist/sync'; // Custom app endpoint

  // Check if customer is logged in
  function isLoggedIn() {
    return window.__st && window.__st.cid;
  }

  function getCustomerId() {
    return window.__st && window.__st.cid;
  }

  // Get wishlist from localStorage
  function getLocalWishlist() {
    try {
      const data = localStorage.getItem(STORAGE_KEY);
      return data ? JSON.parse(data) : [];
    } catch (e) {
      console.error('Error reading wishlist:', e);
      return [];
    }
  }

  // Save wishlist to localStorage
  function saveLocalWishlist(items) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch (e) {
      console.error('Error saving wishlist:', e);
    }
  }

  // Get wishlist (handles both guest and logged-in users)
  function getWishlist() {
    return getLocalWishlist();
  }

  // Check if product is in wishlist
  function isInWishlist(productId) {
    const items = getWishlist();
    return items.some(item => item.id === productId || item.id === String(productId));
  }

  // Add product to wishlist
  function addToWishlist(product) {
    const items = getWishlist();
    const productId = String(product.id);

    // Check if already exists
    if (items.some(item => String(item.id) === productId)) {
      return false;
    }

    // Add product with all necessary info including gout and reviews
    const wishlistItem = {
      id: productId,
      handle: product.handle,
      title: product.title,
      price: product.price,
      compare_at_price: product.compare_at_price || null,
      image: product.image,
      url: product.url,
      type: product.type || null,
      gout: product.gout || null,
      rating: product.rating || 0,
      review_count: product.review_count || 0,
      added_at: new Date().toISOString()
    };

    items.push(wishlistItem);
    saveLocalWishlist(items);

    // Sync to server if logged in
    if (isLoggedIn()) {
      syncToServer(items);
    }

    // Dispatch event
    document.dispatchEvent(new CustomEvent('wishlist:added', {
      detail: { product: wishlistItem, count: items.length }
    }));

    updateWishlistUI();
    return true;
  }

  // Remove product from wishlist
  function removeFromWishlist(productId) {
    let items = getWishlist();
    const initialLength = items.length;

    items = items.filter(item => String(item.id) !== String(productId));

    if (items.length < initialLength) {
      saveLocalWishlist(items);

      // Sync to server if logged in
      if (isLoggedIn()) {
        syncToServer(items);
      }

      // Dispatch event
      document.dispatchEvent(new CustomEvent('wishlist:removed', {
        detail: { productId, count: items.length }
      }));

      updateWishlistUI();
      return true;
    }

    return false;
  }

  // Toggle product in wishlist
  function toggleWishlist(product) {
    if (isInWishlist(product.id)) {
      removeFromWishlist(product.id);
      return false;
    } else {
      addToWishlist(product);
      return true;
    }
  }

  // Get wishlist count
  function getCount() {
    return getWishlist().length;
  }

  // Sync wishlist to server (customer metafield)
  async function syncToServer(items) {
    if (!isLoggedIn()) return;

    try {
      // Use Shopify's customer account API via form submission
      const formData = new FormData();
      formData.append('wishlist', JSON.stringify(items.map(item => ({
        id: item.id,
        handle: item.handle,
        added_at: item.added_at
      }))));

      // Store in a hidden form that updates customer note or use metafield app
      // For now, we rely on localStorage + server-side sync on page load
      console.log('Wishlist synced locally. Server sync requires app.');
    } catch (e) {
      console.error('Error syncing wishlist:', e);
    }
  }

  // Merge guest wishlist with customer wishlist on login
  function mergeWishlists(serverWishlist) {
    const localItems = getLocalWishlist();

    if (!serverWishlist || !Array.isArray(serverWishlist)) {
      return localItems;
    }

    // Create a map of existing items by ID
    const itemMap = new Map();

    // Add server items first
    serverWishlist.forEach(item => {
      itemMap.set(String(item.id), item);
    });

    // Add local items (newer ones will override)
    localItems.forEach(item => {
      const existing = itemMap.get(String(item.id));
      if (!existing || new Date(item.added_at) > new Date(existing.added_at)) {
        itemMap.set(String(item.id), item);
      }
    });

    return Array.from(itemMap.values());
  }

  // Update all wishlist UI elements
  function updateWishlistUI() {
    const count = getCount();

    // Update all counter elements
    document.querySelectorAll('.wishlist-count, [data-wishlist-count]').forEach(el => {
      el.textContent = count;
      el.style.display = count > 0 ? '' : 'none';
    });

    // Update all wishlist buttons
    document.querySelectorAll('[data-wishlist-btn]').forEach(btn => {
      const productId = btn.getAttribute('data-product-id');
      if (productId) {
        const isActive = isInWishlist(productId);
        btn.classList.toggle('is-active', isActive);
        btn.setAttribute('aria-pressed', isActive);
      }
    });

    // Update product page wishlist button
    document.querySelectorAll('.product-custom__wishlist, .product-custom__wishlist-header').forEach(btn => {
      const productId = btn.getAttribute('data-product-id');
      if (productId) {
        const isActive = isInWishlist(productId);
        btn.classList.toggle('is-active', isActive);
      }
    });
  }

  // Initialize wishlist button on product page
  function initProductPageButton() {
    // Support both old class and new inline class
    document.querySelectorAll('.product-custom__wishlist, .product-custom__wishlist-inline, .product-custom__wishlist-header').forEach(btn => {
      if (btn.hasAttribute('data-wishlist-initialized')) return;
      btn.setAttribute('data-wishlist-initialized', 'true');

      const productId = btn.getAttribute('data-product-id');
      if (!productId) return;

      // Set initial state
      if (isInWishlist(productId)) {
        btn.classList.add('is-active');
      }

      // Handle click
      btn.addEventListener('click', function(e) {
        e.preventDefault();
        e.stopPropagation();

        const product = {
          id: this.getAttribute('data-product-id'),
          handle: this.getAttribute('data-product-handle'),
          title: this.getAttribute('data-product-title'),
          price: this.getAttribute('data-product-price'),
          compare_at_price: this.getAttribute('data-product-compare-price'),
          image: this.getAttribute('data-product-image'),
          url: this.getAttribute('data-product-url'),
          type: this.getAttribute('data-product-type'),
          gout: this.getAttribute('data-product-gout'),
          rating: parseFloat(this.getAttribute('data-product-rating')) || 0,
          review_count: parseInt(this.getAttribute('data-product-review-count')) || 0
        };

        console.log('Wishlist click - Product:', product);

        const added = toggleWishlist(product);
        this.classList.toggle('is-active', added);

        // Show feedback
        showToast(added ? 'Ajouté aux favoris' : 'Retiré des favoris');
      });
    });
  }

  // Initialize wishlist buttons on collection/product cards
  function initCardButtons() {
    document.querySelectorAll('[data-wishlist-btn]').forEach(btn => {
      if (btn.hasAttribute('data-wishlist-initialized')) return;
      btn.setAttribute('data-wishlist-initialized', 'true');

      const productId = btn.getAttribute('data-product-id');

      // Set initial state
      if (productId && isInWishlist(productId)) {
        btn.classList.add('is-active');
      }

      btn.addEventListener('click', function(e) {
        e.preventDefault();
        e.stopPropagation();

        const product = {
          id: this.getAttribute('data-product-id'),
          handle: this.getAttribute('data-product-handle'),
          title: this.getAttribute('data-product-title'),
          price: this.getAttribute('data-product-price'),
          compare_at_price: this.getAttribute('data-product-compare-price'),
          image: this.getAttribute('data-product-image'),
          url: this.getAttribute('data-product-url'),
          type: this.getAttribute('data-product-type'),
          gout: this.getAttribute('data-product-gout'),
          rating: parseFloat(this.getAttribute('data-product-rating')) || 0,
          review_count: parseInt(this.getAttribute('data-product-review-count')) || 0
        };

        const added = toggleWishlist(product);
        this.classList.toggle('is-active', added);

        showToast(added ? 'Ajouté aux favoris' : 'Retiré des favoris');
      });
    });
  }

  // Show toast notification
  function showToast(message) {
    // Remove existing toast
    const existing = document.querySelector('.wishlist-toast');
    if (existing) existing.remove();

    const toast = document.createElement('div');
    toast.className = 'wishlist-toast';
    toast.innerHTML = `
      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
      </svg>
      <span>${message}</span>
    `;
    document.body.appendChild(toast);

    // Trigger animation
    requestAnimationFrame(() => {
      toast.classList.add('is-visible');
    });

    // Remove after delay
    setTimeout(() => {
      toast.classList.remove('is-visible');
      setTimeout(() => toast.remove(), 300);
    }, 2500);
  }

  // Clear entire wishlist
  function clearWishlist() {
    saveLocalWishlist([]);
    if (isLoggedIn()) {
      syncToServer([]);
    }
    updateWishlistUI();
    document.dispatchEvent(new CustomEvent('wishlist:cleared'));
  }

  // Initialize
  function init() {
    console.log('Wishlist: Initializing...');

    // Update UI on load
    updateWishlistUI();

    // Init buttons
    initProductPageButton();
    initCardButtons();

    // Re-init on dynamic content load (for infinite scroll, etc.)
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.addedNodes.length) {
          initProductPageButton();
          initCardButtons();
        }
      });
    });

    observer.observe(document.body, { childList: true, subtree: true });

    // Listen for customer login (page reload usually)
    // The server will inject customer wishlist data if available
    if (window.customerWishlistData) {
      const merged = mergeWishlists(window.customerWishlistData);
      saveLocalWishlist(merged);
      syncToServer(merged);
      updateWishlistUI();
    }

    console.log('Wishlist: Initialized. Found buttons:', document.querySelectorAll('.product-custom__wishlist').length);
  }

  // Auto-init on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Also re-init on Shopify section events
  document.addEventListener('shopify:section:load', init);

  // Public API
  return {
    get: getWishlist,
    add: addToWishlist,
    remove: removeFromWishlist,
    toggle: toggleWishlist,
    has: isInWishlist,
    count: getCount,
    clear: clearWishlist,
    update: updateWishlistUI
  };
})();
